/**
 * @fileoverview Componente responsável por criar e editar status de leads.
 */

import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { Colorize } from "@material-ui/icons";
import { ColorBox } from 'material-ui-color';

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '24px',
      background: theme.palette.type === 'dark'
        ? 'linear-gradient(135deg, rgba(18, 18, 19, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
        : '#FFFFFF',
      boxShadow: theme.palette.type === 'dark'
        ? '0 24px 80px rgba(0, 0, 0, 0.6)'
        : '0 24px 80px rgba(0, 0, 0, 0.12)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : 'none',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: theme.palette.type === 'dark'
          ? 'linear-gradient(90deg, #5B7CFF 0%, #9D7CFF 100%)'
          : 'linear-gradient(90deg, #3D5CFD 0%, #7C3AED 100%)',
        borderRadius: '24px 24px 0 0',
      },
    },
    '& .MuiBackdrop-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)',
    },
  },
  dialogTitle: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.15)'
      : '1px solid rgba(0, 0, 0, 0.08)',
    '& .MuiTypography-root': {
      fontSize: '1.5rem',
      fontWeight: 700,
    },
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
    marginTop: theme.spacing(2),
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '& fieldset': {
        borderColor: 'transparent',
      },
      '&:hover': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(61, 92, 253, 0.04)',
      },
      '&.Mui-focused': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 1)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.4)' : '1px solid rgba(61, 92, 253, 0.3)',
        boxShadow: '0 0 0 4px rgba(91, 124, 255, 0.15)',
      },
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  colorAdorment: {
    width: 24,
    height: 24,
    borderRadius: '6px',
    border: '2px solid rgba(255, 255, 255, 0.2)',
  },
  checkboxContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  colorPickerContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
  },
}));

const StatusSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Nome muito curto")
    .max(50, "Nome muito longo")
    .required("Nome é obrigatório"),
  color: Yup.string()
    .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, "Cor inválida")
    .required("Cor é obrigatória"),
});

const LeadStatusModal = ({ open, onClose, statusId }) => {
  const classes = useStyles();
  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);

  const initialState = {
    name: "",
    color: "#3D5CFD",
    isDefault: false,
    isCompleted: false,
    isPending: false,
    showInDashboard: false,
  };

  const [status, setStatus] = useState(initialState);

  useEffect(() => {
    if (!open) {
      setStatus(initialState);
      setColorPickerModalOpen(false);
      return;
    }

    const fetchStatus = async () => {
      if (!statusId) return;

      try {
        const { data } = await api.get(`/lead-statuses`);
        const foundStatus = data.find(s => s.id === statusId);
        if (foundStatus) {
          setStatus({
            name: foundStatus.name,
            color: foundStatus.color,
            isDefault: foundStatus.isDefault,
            isCompleted: foundStatus.isCompleted,
            isPending: foundStatus.isPending,
            showInDashboard: foundStatus.showInDashboard || false,
          });
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchStatus();
  }, [statusId, open]);

  const handleClose = () => {
    setStatus(initialState);
    setColorPickerModalOpen(false);
    onClose();
  };

  const handleSaveStatus = async (values) => {
    const payload = { ...values, showInDashboard: true };
    try {
      if (statusId) {
        await api.put(`/lead-statuses/${statusId}`, payload);
        toast.success("Coluna atualizada com sucesso!");
      } else {
        await api.post("/lead-statuses", payload);
        toast.success("Coluna criada com sucesso!");
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
        className={classes.dialog}
      >
        <DialogTitle className={classes.dialogTitle}>
          {statusId ? "Editar Coluna" : "Nova Coluna"}
        </DialogTitle>
        <Formik
          initialValues={status}
          enableReinitialize={true}
          validationSchema={StatusSchema}
          validateOnChange={false}
          validateOnBlur={true}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveStatus(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label="Nome da Coluna"
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  placeholder="Ex: Pendente"
                />

                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Cor"
                    name="color"
                    error={touched.color && Boolean(errors.color)}
                    helperText={touched.color && errors.color}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <div
                            style={{ backgroundColor: values.color }}
                            className={classes.colorAdorment}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <IconButton
                          size="small"
                          onClick={() => setColorPickerModalOpen(!colorPickerModalOpen)}
                        >
                          <Colorize />
                        </IconButton>
                      ),
                    }}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                </div>

                {colorPickerModalOpen && (
                  <div className={classes.colorPickerContainer}>
                    <ColorBox
                      disableAlpha={true}
                      hslGradient={false}
                      value={values.color}
                      onChange={(val) => {
                        setFieldValue('color', `#${val.hex}`);
                        setStatus(prev => ({ ...prev, color: `#${val.hex}` }));
                      }}
                    />
                  </div>
                )}

                <div style={{ marginTop: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!values.isCompleted}
                        onChange={(e) => setFieldValue("isCompleted", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Marcar como coluna concluída"
                  />
                  <p style={{ margin: "0 0 0 2px", fontSize: 12, opacity: 0.65, lineHeight: 1.4 }}>
                    Cards nesta coluna aparecem riscados e esmaecidos no Calendário (status finalizado).
                  </p>
                </div>

              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {statusId ? "Salvar" : "Criar"}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default LeadStatusModal;
